import {
  DATE_RANGE_OPTIONS,
  type DateRangeOption,
  type IsoDateRange,
  isIsoDate,
  type QuickRange,
  resolveQuickRange,
  type ScheduleMode,
} from '@/utils/date-ranges';

export interface FilterSelection {
  range: DateRangeOption;
  from: string | null;
  to: string | null;
  forms: number[];
  hierarchy: number[];
}

const DEFAULT_RANGE: QuickRange = 'next12Months';

export const DEFAULT_FILTER_SELECTION: FilterSelection = {
  range: DEFAULT_RANGE,
  from: null,
  to: null,
  forms: [],
  hierarchy: [],
};

interface RawSelection {
  range?: unknown;
  from?: unknown;
  to?: unknown;
  forms?: unknown;
  hierarchy?: unknown;
}

const toIds = (value: unknown) =>
  Array.isArray(value) ? value.filter((id): id is number => Number.isInteger(id)) : [];

const toIsoDateOrNull = (value: unknown) => (typeof value === 'string' && isIsoDate(value) ? value : null);

// Anything unusable falls back to the default range, so a stale URL or stored value still opens the page.
export const normalizeSelection = (raw: RawSelection): FilterSelection => {
  const range = DATE_RANGE_OPTIONS.find((option) => option === raw.range) ?? DEFAULT_RANGE;
  const from = toIsoDateOrNull(raw.from);
  const to = toIsoDateOrNull(raw.to);
  const isCustom = range === 'custom' && from !== null && to !== null;

  return {
    range: range === 'custom' && !isCustom ? DEFAULT_RANGE : range,
    from: isCustom ? from : null,
    to: isCustom ? to : null,
    forms: toIds(raw.forms),
    hierarchy: toIds(raw.hierarchy),
  };
};

export const parseStoredSelection = (stored: string): FilterSelection | null => {
  if (!stored) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(stored);
    return typeof parsed === 'object' && parsed !== null ? normalizeSelection(parsed) : null;
  } catch {
    return null;
  }
};

export const resolveSelectionDates = (selection: FilterSelection, mode: ScheduleMode, today: Date): IsoDateRange => {
  if (selection.range === 'custom' && selection.from && selection.to) {
    return { from: selection.from, to: selection.to };
  }
  return resolveQuickRange(selection.range === 'custom' ? DEFAULT_RANGE : selection.range, mode, today);
};

const sameIds = (left: number[], right: number[]) => {
  const rightIds = new Set(right);
  return left.length === right.length && left.every((id) => rightIds.has(id));
};

export const isSameSelection = (left: FilterSelection, right: FilterSelection) =>
  left.range === right.range &&
  left.from === right.from &&
  left.to === right.to &&
  sameIds(left.forms, right.forms) &&
  sameIds(left.hierarchy, right.hierarchy);
